import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { LanguageModel, LanguageResponse } from "model";

import { fetchRequestAttribute } from "~utils";

import { computeInitialLanguageSelected } from "./utils";

export function useLanguages(initialLanguageSelected: string[], testData?: LanguageModel[]) {
  // validatedInitialData here takes the user's preferred languages
  // and coerces it into the list that the language renderer will accept. Since
  // there are many existing records of languages being transacted
  // in that don't fit the list, these values will get sorted into "Other"

  const [validatedInitialData, setValidatedInitialData] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);

  const { data: languagesData } = useQuery({
    queryKey: ["fetchRequestAttributeLanguages"],
    queryFn: async () => {
      const data = await fetchRequestAttribute<LanguageResponse>({ key: "languages" });
      const languagesData = data?.data?.options || [];

      const validLanguages = languagesData.map((item) => item.label);
      const validInitialData = computeInitialLanguageSelected(
        validLanguages,
        initialLanguageSelected,
      );
      setValidatedInitialData(validInitialData);
      setSelected(validInitialData);
      return languagesData;
    },
    enabled: !testData, // if testData exists, do not execute the hook api call
    initialData: [],
  });

  // Update selected languages when initialLanguageSelected changes
  useEffect(() => {
    if (languagesData?.length > 0) {
      const validLanguages = languagesData.map((item) => item.label);
      const validInitialData = computeInitialLanguageSelected(
        validLanguages,
        initialLanguageSelected,
      );
      setValidatedInitialData(validInitialData);
      setSelected(validInitialData);
    }
  }, [initialLanguageSelected, languagesData]);

  if (testData) return { selected, setSelected, validatedInitialData, languagesData: testData };

  return { selected, setSelected, validatedInitialData, languagesData };
}
